import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChatMessage,
  VerificationSummary,
  WebSearchStatus,
} from '../../types/chat';
import SearchResultsCard, { SearchResult } from './SearchResultsCard';
import HighlightedContent from './HighlightedContent';
import ChatVerificationBadge from './ChatVerificationBadge';
import ChatAssistantAvatar from './ChatAssistantAvatar';
import ChatCopyButton from './ChatCopyButton';
import ChatNotice from './ChatNotice';
import ChatSystemMessage from './ChatSystemMessage';
import ChatThinkingRow from './ChatThinkingRow';
import ChatUserMessage from './ChatUserMessage';
import MarkdownRenderer, { isMarkdownContent } from '../common/MarkdownRenderer';
import { expandLiteralValues } from '../../utils/literalValues';

// Extended message type with search results and grounding
export interface ExtendedChatMessage extends Omit<ChatMessage, 'searchResults'> {
  searchResults?: {
    query: string;
    results: SearchResult[];
  };
  groundingStatus?: string;
  verificationSummary?: VerificationSummary;
  webSearchStatus?: WebSearchStatus;
}

interface NotionStyleMessageListProps {
  messages: ExtendedChatMessage[];
  isLoading: boolean;
  onSearchResultClick?: (result: SearchResult, query: string) => void;
  onAddToContext?: (results: SearchResult[]) => Promise<void>;
  selectedFileIds?: string[];
  onRetryWithMode?: (message: string) => void;
}

const MARKDOWN_CLASS =
  'leading-[1.7] [&_p]:my-2 [&_p:first-of-type]:mt-0 [&_p:last-child]:mb-0';

/** The question this answer was given to, for the retry-in-another-mode link. */
const precedingQuestion = (messages: ExtendedChatMessage[], from: number): string | null => {
  for (let i = from - 1; i >= 0; i--) {
    if (messages[i].role === 'user') return messages[i].content;
  }
  return null;
};

const NotionStyleMessageList: React.FC<NotionStyleMessageListProps> = ({
  messages,
  isLoading,
  onSearchResultClick,
  onAddToContext,
  selectedFileIds,
  onRetryWithMode,
}) => {
  const { t } = useTranslation();

  // One seam: resolving here keeps every renderer below reading plain `.content`,
  // and re-runs when `t` changes so a visible bubble follows the switch.
  const localizedMessages = useMemo(
    () => messages.map((message) => {
      if (!message.contentKey && !message.noticeKey) return message;
      // `content` wins: every contentKey writer sets content:"" , so a later write
      // that fills content (a completed summary) is self-healing against a stale key.
      const body = message.content
        || (message.contentKey ? t(message.contentKey.messageKey, message.contentKey.values) : '');
      const notice = message.noticeKey ? t(message.noticeKey) : '';
      const content = notice && body ? `${body}\n\n${notice}` : notice || body;
      return { ...message, content };
    }),
    [messages, t],
  );
  // Which messages currently show their per-sentence verification tints.
  const [highlightedMsgIds, setHighlightedMsgIds] = useState<Set<string>>(new Set());

  const toggleHighlight = (messageId: string, open: boolean) =>
    setHighlightedMsgIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(messageId);
      else next.delete(messageId);
      return next;
    });

  const renderAssistantMessage = (message: ExtendedChatMessage, msgIndex: number) => {
    const annotations = message.verificationSummary?.sentence_annotations;
    // Completeness values stay React text nodes, one element each: never through content or the markdown renderer.
    const missingValues = message.completeness?.missing;
    const notEvaluatedValues = message.completeness?.not_evaluated;
    const valueChips = (values: string[]) =>
      values.map((value, index) => (
        <span key={index} className="mx-0.5 rounded bg-muted px-1 font-mono">
          {value}
        </span>
      ));
    const question = onRetryWithMode ? precedingQuestion(localizedMessages, msgIndex) : null;
    const plainText = expandLiteralValues(message.content, message.literalValues);

    return (
      <div key={message.id} className="group mb-6 flex w-full gap-3">
        <ChatAssistantAvatar />

        <div className="min-w-0 flex-1">
          {highlightedMsgIds.has(message.id) && annotations?.length ? (
            <HighlightedContent
              content={message.content}
              annotations={annotations}
              literalValues={message.literalValues}
            />
          ) : isMarkdownContent(message.content) ? (
            <MarkdownRenderer
              content={message.content}
              variant="body2"
              className={MARKDOWN_CLASS}
              literalValues={message.literalValues}
            />
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-[1.7]">
              {plainText}
            </p>
          )}

          {message.searchResults?.results && message.searchResults.results.length > 0 && (
            <div className="mt-4">
              <SearchResultsCard
                query={message.searchResults.query}
                results={message.searchResults.results}
                onResultClick={onSearchResultClick}
                onAddToContext={onAddToContext}
                selectedFileIds={selectedFileIds}
              />
            </div>
          )}

          {/* Shown at message level because SearchResultsCard may not render */}
          {message.groundingStatus === 'general_fallback' && (
            <ChatNotice tone="warning">{t('chat.generalFallbackWarning')}</ChatNotice>
          )}

          {Array.isArray(missingValues) && missingValues.length > 0 && (
            <ChatNotice tone="warning" className="break-words">
              {t('chat.completenessMissingNotice')} {valueChips(missingValues)}
            </ChatNotice>
          )}
          {Array.isArray(notEvaluatedValues) && notEvaluatedValues.length > 0 && (
            <ChatNotice tone="warning" className="break-words">
              {t('chat.completenessNotEvaluatedNotice')} {valueChips(notEvaluatedValues)}
            </ChatNotice>
          )}

          {message.webSearchStatus?.status === 'disabled' && (
            <ChatNotice tone="info">{t('chat.webSearchDisabledNotice')}</ChatNotice>
          )}

          {message.verificationSummary && (
            <ChatVerificationBadge
              summary={message.verificationSummary}
              onToggle={(open) => toggleHighlight(message.id, open)}
            />
          )}

          {message.verificationSummary?.level === 'warning' &&
            message.groundingStatus !== 'general_fallback' && (
              <ChatNotice tone="warning">{t('chat.verificationNeedsReview')}</ChatNotice>
            )}

          {question && (
            <button
              type="button"
              onClick={() => onRetryWithMode!(question)}
              className="mt-2 block w-full text-right text-[0.7rem] text-primary hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('chat.retryWithMode')}
            </button>
          )}
        </div>

        <ChatCopyButton content={plainText} />
      </div>
    );
  };

  const renderMessage = (message: ExtendedChatMessage, msgIndex: number) => {
    if (message.role === 'system' || message.content.startsWith('✅')) {
      return (
        <ChatSystemMessage key={message.id} content={expandLiteralValues(message.content, message.literalValues)} />
      );
    }
    if (message.role === 'user') {
      return <ChatUserMessage key={message.id} content={message.content} />;
    }
    return renderAssistantMessage(message, msgIndex);
  };

  return (
    <div>
      {localizedMessages.map(renderMessage)}

      <ChatThinkingRow isLoading={isLoading} />

      {messages.length === 0 && !isLoading && (
        <p className="mt-8 px-4 text-center text-sm leading-relaxed text-muted-foreground">
          {t('chat.notionEmptyDescription')}
        </p>
      )}
    </div>
  );
};

export default NotionStyleMessageList;
